import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, X } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const EMPTY = { slug: "", title: "", category: "Buying Guides", excerpt: "", cover: "", author: "Ebikes&Gear Team", read_time: "5 min read", tags: "", meta_description: "", content: "" };

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...editing, tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : editing.tags };
    try {
      if (editing._isNew) await api.post("/blog", payload);
      else await api.put(`/blog/${editing._origSlug}`, payload);
      toast.success("Article saved");
      setEditing(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (slug) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/blog/${slug}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">{posts.length} articles</h2>
        <button onClick={() => setEditing({ ...EMPTY, _isNew: true })} className="btn-primary !py-2.5 text-sm" data-testid="admin-add-blog"><Plus size={18} /> New article</button>
      </div>

      <div className="mt-5 space-y-3">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4" data-testid={`admin-blog-${p.slug}`}>
            <img src={p.cover} alt={p.title} className="h-14 w-20 rounded-lg object-cover" />
            <div className="flex-1 min-w-0">
              <p className="font-bold truncate">{p.title}</p>
              <p className="text-xs text-muted-foreground">{p.category} · {p.read_time}</p>
            </div>
            <button onClick={() => setEditing({ ...p, _origSlug: p.slug, tags: (p.tags || []).join(", ") })} className="grid h-9 w-9 place-items-center rounded-lg border border-border hover:border-primary hover:text-primary" aria-label="Edit"><PencilSimple size={16} /></button>
            <button onClick={() => del(p.slug)} className="grid h-9 w-9 place-items-center rounded-lg border border-border hover:border-primary hover:text-primary" aria-label="Delete"><Trash size={16} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] grid place-items-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={save} className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-soft-lg" data-testid="blog-editor">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-black">{editing._isNew ? "New article" : "Edit article"}</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close"><X size={22} /></button>
            </div>
            <div className="mt-5 space-y-3">
              <ImageUpload value={editing.cover} onUploaded={(url) => setEditing({ ...editing, cover: url })} label="Cover image" testid="blog-cover-upload" />
              <div className="grid gap-3 sm:grid-cols-2">
                <input required placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
                <input required placeholder="Slug" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
                <input placeholder="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
                <input placeholder="Read time" value={editing.read_time} onChange={(e) => setEditing({ ...editing, read_time: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
                <input placeholder="Author" value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
                <input placeholder="Tags (comma separated)" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className="rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              </div>
              <input placeholder="Excerpt" value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <input placeholder="Meta description (SEO)" value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
              <textarea rows={8} placeholder="Article content (separate paragraphs with a blank line)" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} className="w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none focus:border-primary" />
            </div>
            <button className="btn-primary mt-5 w-full" data-testid="blog-save">Save article</button>
          </form>
        </div>
      )}
    </div>
  );
}
